<template>
  <div class="relative pricing-card">
    <!-- Current Plan Badge -->
    <div
      v-if="isCurrentPlan"
      class="absolute -top-6 left-1/2 transform -translate-x-1/2 z-10"
    >
      <div class="bg-gradient-to-r from-emerald-600 to-teal-600 text-white px-6 py-2 rounded-full text-sm font-semibold shadow-lg">
        <svg
          class="w-4 h-4 inline mr-2"
          fill="currentColor"
          viewBox="0 0 20 20"
        >
          <path
            fill-rule="evenodd"
            d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
            clip-rule="evenodd"
          />
        </svg>
        <span v-text="t('Current Plan')" />
      </div>
    </div>

    <!-- Recommended Badge -->
    <div
      v-else-if="plan.is_recommended"
      class="absolute -top-6 left-1/2 transform -translate-x-1/2 z-10"
    >
      <div class="bg-gradient-to-r from-blue-600 to-indigo-600 text-white px-6 py-2 rounded-full text-sm font-semibold shadow-lg">
        <svg
          class="w-4 h-4 inline mr-2"
          fill="currentColor"
          viewBox="0 0 20 20"
        >
          <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
        </svg>
        <span v-text="t('Recommended')" />
      </div>
    </div>

    <div
      class="backdrop-blur-xl rounded-3xl p-8 md:p-10 hover:shadow-2xl transition-all duration-300 transform hover:-translate-y-2 relative overflow-hidden"
      :class="isCurrentPlan
        ? 'bg-white/95 border-2 border-emerald-200 shadow-2xl shadow-emerald-100/50 ring-1 ring-emerald-100'
        : plan.is_recommended
          ? 'bg-white/90 border-2 border-blue-200 shadow-2xl shadow-blue-100/50 ring-1 ring-blue-100'
          : 'bg-white/80 border border-white/20 shadow-xl'"
    >
      <!-- Background Gradient for Current Plan -->
      <div
        v-if="isCurrentPlan"
        class="absolute inset-0 bg-gradient-to-br from-emerald-50/50 to-teal-50/50 rounded-3xl"
      />

      <!-- Background Gradient for Recommended -->
      <div
        v-else-if="plan.is_recommended"
        class="absolute inset-0 bg-gradient-to-br from-blue-50/50 to-indigo-50/50 rounded-3xl"
      />

      <div class="relative z-10">
        <!-- Plan Header -->
        <div class="text-center mb-8">
          <h3
            class="text-2xl font-bold text-gray-900 mb-2"
            v-text="plan.name"
          />

          <!-- Price -->
          <div class="mb-6">
            <div class="flex items-baseline justify-center space-x-2">
              <!-- Discounted Price Display -->
              <template v-if="hasDiscount">
                <!-- Original Price (Strikethrough) -->
                <span
                  class="text-2xl md:text-3xl font-semibold text-gray-400 line-through opacity-75"
                  v-text="plan.price_formatted_with_symbol"
                />
                <!-- Discounted Price -->
                <span
                  class="text-4xl md:text-5xl font-extrabold whitespace-nowrap"
                  :class="isCurrentPlan
                    ? 'bg-gradient-to-r from-emerald-600 to-teal-600 bg-clip-text text-transparent'
                    : plan.is_recommended
                      ? 'bg-gradient-to-r from-blue-600 to-indigo-600 bg-clip-text text-transparent'
                      : 'text-gray-900'"
                  v-text="plan.discounted_price_formatted_with_symbol"
                />
              </template>
              <!-- Regular Price Display -->
              <template v-else>
                <span
                  class="text-5xl md:text-6xl font-extrabold whitespace-nowrap"
                  :class="isCurrentPlan
                    ? 'bg-gradient-to-r from-emerald-600 to-teal-600 bg-clip-text text-transparent'
                    : plan.is_recommended
                      ? 'bg-gradient-to-r from-blue-600 to-indigo-600 bg-clip-text text-transparent'
                      : 'text-gray-900'"
                  v-text="plan.price_formatted_with_symbol"
                />
              </template>
              <span
                class="text-xl text-gray-500 font-medium"
                v-text="t('WHT')"
              />
            </div>
            <p
              class="text-gray-500 mt-2"
              v-text="hasDiscount
                ? t('{price} WHT per year', { price: plan.discounted_price_formatted_with_symbol })
                : t('{price} WHT per year', { price: plan.price_formatted_with_symbol })"
            />
            <!-- Savings Badge -->
            <div
              v-if="hasDiscount"
              class="mt-3 inline-flex items-center px-3 py-1 rounded-full text-sm font-semibold"
              :class="isCurrentPlan
                ? 'bg-gradient-to-r from-emerald-100 to-teal-100 text-emerald-800'
                : plan.is_recommended
                  ? 'bg-gradient-to-r from-blue-100 to-indigo-100 text-blue-800'
                  : 'bg-green-100 text-green-800'"
            >
              <svg
                class="w-4 h-4 mr-1"
                fill="currentColor"
                viewBox="0 0 20 20"
              >
                <path
                  fill-rule="evenodd"
                  d="M4 4a2 2 0 00-2 2v4a2 2 0 002 2V6h10a2 2 0 00-2-2H4zm2 6a2 2 0 012-2h8a2 2 0 012 2v4a2 2 0 01-2 2H8a2 2 0 01-2-2v-4zm6 4a2 2 0 100-4 2 2 0 000 4z"
                  clip-rule="evenodd"
                />
              </svg>
              <span v-text="savingsText" />
            </div>
          </div>
        </div>

        <!-- Features List -->
        <ul class="space-y-4 mb-10">
          <li
            v-for="(feature, index) in plan.features"
            :key="index"
            class="flex items-center space-x-3"
          >
            <!-- Check Icon -->
            <div
              class="flex-shrink-0 w-6 h-6 rounded-full flex items-center justify-center"
              :class="isCurrentPlan
                ? 'bg-gradient-to-r from-emerald-500 to-teal-500'
                : plan.is_recommended
                  ? 'bg-gradient-to-r from-blue-500 to-indigo-500'
                  : 'bg-green-500'"
            >
              <svg
                class="w-4 h-4 text-white"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  stroke-width="2"
                  d="M5 13l4 4L19 7"
                />
              </svg>
            </div>
            <span class="text-gray-700 font-medium">
              <span v-text="feature.label" /> : <span
                class="text-gray-900 font-semibold"
                v-text="feature.value"
              />
            </span>
          </li>
        </ul>

        <!-- Past Due Subscription Warning -->
        <div
          v-if="isCurrentPlan && !currentSubscriptionActive"
          class="bg-amber-50/80 border border-amber-200 rounded-lg p-4 mb-6 backdrop-blur-sm"
        >
          <div class="flex items-start">
            <div class="flex-shrink-0">
              <svg
                class="h-5 w-5 text-amber-400"
                viewBox="0 0 20 20"
                fill="currentColor"
              >
                <path
                  fill-rule="evenodd"
                  d="M8.485 2.495c.673-1.167 2.357-1.167 3.03 0l6.28 10.875c.673 1.167-.178 2.625-1.516 2.625H3.72c-1.337 0-2.189-1.458-1.515-2.625L8.485 2.495zM10 5a.75.75 0 01.75.75v3.5a.75.75 0 01-1.5 0v-3.5A.75.75 0 0110 5zm0 9a1 1 0 100-2 1 1 0 000 2z"
                  clip-rule="evenodd"
                />
              </svg>
            </div>
            <div class="ml-3">
              <p 
                class="text-sm font-medium text-amber-800"
                v-text="t('Your subscription is past due or has incomplete payment')"
              />
              <p 
                class="text-sm text-amber-700 mt-1"
                v-text="t('Use the button below to complete the payment')"
              />
            </div>
          </div>
        </div>

        <!-- CTA Button -->
        <button
          class="disabled:cursor-not-allowed disabled:opacity-50 group w-full inline-flex items-center justify-center px-8 py-4 font-semibold rounded-xl transition-all duration-300 transform hover:-translate-y-1"
          :class="isCurrentPlan
            ? 'bg-gradient-to-r from-emerald-600 to-teal-600 text-white cursor-default'
            : plan.is_recommended
              ? 'bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 shadow-lg hover:shadow-xl text-white'
              : 'bg-gray-900 hover:bg-gray-800 text-white'"
          :disabled="isPlanDisabled(plan)"
          @click.prevent="selectPlan(plan)"
        >
          <template v-if="isCurrentPlan">
            <svg
              class="w-5 h-5 mr-2"
              fill="currentColor"
              viewBox="0 0 20 20"
            >
              <path
                fill-rule="evenodd"
                d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                clip-rule="evenodd"
              />
            </svg>
            <span v-text="currentPlanButtonText()" />
          </template>
          <template v-else>
            <span v-text="t('Subscribe')" />
            <svg
              class="w-5 h-5 ml-2 group-hover:translate-x-1 transition-transform duration-200"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                stroke-linecap="round"
                stroke-linejoin="round"
                stroke-width="2"
                d="M13 7l5 5m0 0l-5 5m5-5H6"
              />
            </svg>
          </template>
        </button>

        <!-- Trust Indicator -->
        <div class="flex items-center justify-center mt-6 text-sm text-gray-500">
          <svg
            class="w-4 h-4 mr-2 text-green-500"
            fill="currentColor"
            viewBox="0 0 20 20"
          >
            <path
              fill-rule="evenodd"
              d="M5 9V7a5 5 0 0110 0v2a2 2 0 012 2v5a2 2 0 01-2 2H5a2 2 0 01-2-2v-5a2 2 0 012-2zm8-2v2H7V7a3 3 0 016 0z"
              clip-rule="evenodd"
            />
          </svg>
          <span v-text="t('Secure payments')" />
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import { usePage } from '@inertiajs/vue3';
import useBackendRouting from '@/composables/useBackendRouting';

interface Plan {
  id: number;
  name: string;
  stripe_price: string,
  is_recommended: boolean,
  price_formatted: string;
  price_formatted_with_symbol: string;
  discounted_price_formatted_with_symbol?: string;
  savings_text?: string;
  price: number;
  order: number;
  features: {
    label: string;
    value: string;
  }[];
}

const { t } = useI18n();
const { route } = useBackendRouting();

const props = withDefaults(defineProps<{
  plan: Plan;
  hasDiscount?: boolean;
  discountedPrice?: number | string;
}>(), {
  hasDiscount: false,
  discountedPrice: 0,
});

const currentSubscriptionPlan = computed(() => usePage().props.currentSubscriptionPlan as Plan | null);
const currentSubscriptionActive = computed(() => usePage().props.currentSubscriptionActive  as boolean || false);
const currentSubscriptionHostedInvoiceUrl = computed(() => usePage().props.currentSubscriptionHostedInvoiceUrl as string | null);

const isCurrentPlan = computed(() => {
  if (!currentSubscriptionPlan.value) {
    return false;
  }
  return currentSubscriptionPlan.value.id === props.plan.id;
});

const savingsText = computed(() => props.plan.savings_text || '');

const canUpgrade = (plan: Plan) => !currentSubscriptionPlan.value || (plan.order > currentSubscriptionPlan.value?.order);
const canCompletePayment = () => currentSubscriptionPlan.value && !currentSubscriptionActive.value;

function isPlanDisabled(plan: Plan) {
  return isCurrentPlan.value
    ? !canCompletePayment()
    : !canUpgrade(plan);
}

function selectPlan(plan: Plan) {
  const redirectTo = canCompletePayment()
    ? currentSubscriptionHostedInvoiceUrl.value
    : canUpgrade(plan)
      ? route('subscription_checkout', { price: plan.stripe_price })
      : null;

  if (redirectTo) {
    window.location.href = redirectTo;
  }
}

function currentPlanButtonText() {
  return canCompletePayment()
    ? t('Complete payment')
    : t('Current Active Plan');
}
</script>
